/**
 * Shared formatting utilities for assets in diffs and documentation.
 * This is the single source of truth for asset formatting to prevent drift
 * between diff output and schema documentation.
 */

import type { AssetDescription } from '../types';
import { stringifyUnknown } from 'js/src/libs/custom-utils';

/**
 * Format a millisecond duration for display, rounding to the nearest millisecond
 * to avoid noisy floating-point values like "52833.54166666667ms".
 */
export function formatDurationMs(durationMs: number): string {
    return `${Math.round(durationMs)}ms`;
}

/**
 * Helper to format property values for display.
 *
 * Values are rendered in full — never truncated. The agent can only see (and, for tools that must
 * restate a whole value rather than patch it, act on) content that reaches it through this
 * serialized state, so a clipped tail is content it can neither read nor safely reproduce. Guarding
 * against a pathologically large state is done where the state is injected (see
 * isContextMessageOversized), rather than by silently dropping the tail of individual fields here.
 */
function formatPropertyValue(value: unknown): string {
    if (typeof value === 'string') {
        return `"${value}"`;
    }
    return JSON.stringify(value);
}

/**
 * Format an asset in YAML-style format for diffs and documentation.
 * This is the canonical format used throughout the codebase.
 *
 * @param asset The asset to format
 * @param prefix The line prefix (e.g., '+ ', '- ', or '' for plain format)
 * @returns Formatted asset string with all properties
 *
 * @example
 * ```typescript
 * // For git diff additions:
 * formatAsset(asset, '+ ')
 * // Output:
 * // + - type: MEDIA
 * // +   name: media_2
 * // +   bounds: "0 0 1920 1080"
 * // +   mediaId: "12345"
 *
 * // For schema documentation:
 * formatAsset(exampleAsset, '')
 * // Output:
 * // - type: MEDIA
 * //   name: <asset_name>
 * //   bounds: <x y width height>
 * ```
 */
export function formatAsset(asset: AssetDescription, prefix: string = ''): string {
    let output = '';

    // Header: type and name (following YAML list syntax)
    output += `${prefix}- type: ${asset.type}\n`;
    output += `${prefix}  name: ${asset.name}\n`;

    // Special handling for subscene (appears before other properties)
    if ('subscene' in asset && asset.subscene !== undefined) {
        output += `${prefix}  subscene: ${asset.subscene}\n`;
    }

    // All other properties (excluding name, type, subscene)
    for (const [propKey, propValue] of Object.entries(asset)) {
        if (propKey === 'name' || propKey === 'type' || propKey === 'subscene') {
            continue;
        }
        output += `${prefix}  ${propKey}: ${formatPropertyValue(propValue)}\n`;
    }

    return output;
}

/**
 * Format just the asset header (type and name) - useful for modified assets
 * where we only need to identify the asset without showing all properties.
 *
 * @param asset Object with type and name properties
 * @param prefix The line prefix (e.g., '  ' for neutral, '+ ' for added, '- ' for removed)
 * @returns Formatted header string
 */
export function formatAssetHeader(
    asset: Pick<AssetDescription, 'type' | 'name'>,
    prefix: string = '',
): string {
    return `${prefix}- type: ${asset.type}\n${prefix}  name: ${asset.name}\n`;
}

/**
 * Format all properties of an object as key-value pairs.
 * Used for formatting video, template, music, and scene metadata.
 *
 * @param obj The object to format
 * @param prefix Line prefix (e.g., '+ ', '- ', or '' for plain format)
 * @param excludeKeys Keys to exclude from output
 * @returns Formatted string with one property per line
 *
 * @example
 * ```typescript
 * const video = { resolution: '1920x1080', aspectRatio: '16:9', duration: '5000ms' };
 * formatProperties(video, '+ ');
 * // Output:
 * // + resolution: 1920x1080
 * // + aspectRatio: 16:9
 * // + duration: 5000ms
 * ```
 */
export function formatProperties(
    obj: Record<string, unknown>,
    prefix: string = '',
    excludeKeys: string[] = [],
): string {
    let output = '';
    for (const [key, value] of Object.entries(obj)) {
        if (excludeKeys.includes(key)) {
            continue;
        }
        // null and undefined are spelled out rather than blanked, so a key that is present
        // with no value stays distinguishable from one holding an empty string.
        const formattedValue = value == null ? String(value) : stringifyUnknown(value);
        output += `${prefix}${key}: ${formattedValue}\n`;
    }
    return output;
}
